import { type WebConfig } from '@koala/sdk';
import Head from 'next/head';
import { shouldNoindexOrderingPages } from '@/utils/config';

interface Props {
  config: WebConfig;
}

/**
 * Opts a page out of search indexing for brands that have asked for it, so the ordering
 * pages stop competing in search with the brand's canonical marketing `/location/<slug>`
 * pages, which are served from the same origin behind Caddy.
 *
 * `follow`, not `nofollow`: the page itself should drop out of the index, but the internal
 * links it carries to `/location/*` should keep passing equity.
 *
 * Renders nothing unless the brand has opted in, so no robots directive is emitted — and
 * no existing tag changes — for every other brand. Intentionally not gated on
 * `ENV === 'production'` the way the canonical in `./site.tsx` is: a robots directive is
 * host-independent, non-production hosts are already covered by the blanket `Disallow: /`
 * in `pages/api/robots.ts`, and gating it would leave QA unable to verify this anywhere
 * but production.
 *
 * @see https://chowly.atlassian.net/browse/TEAM-14137
 */
export function MetaRobots({ config }: Props) {
  if (!shouldNoindexOrderingPages(config)) {
    return null;
  }

  return (
    <Head>
      <meta name="robots" content="noindex,follow" key="robots" />
    </Head>
  );
}
